import {
  Box,
  Button,
  Grid,
  Hidden,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import React, { useEffect } from "react";
import {
  MultiFiltersType,
  filterColumnType,
} from "../../pages/Worktype/Worktype";

import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";

import { CSVLink } from "react-csv";

import axiosInstance from "../../api";

type ManualExportPropsType = {
  totalRows: number;
  loading: boolean;
  columns: GridColDef<any>[];
  rows: readonly any[];
  setFilterData: any;
  filterData: any;
  csvHeaders: any;
  currentPage: number;
  exportCSVUrl: string;
  pageLimit: number;
  defaultCSVExport: false;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageLimit: React.Dispatch<React.SetStateAction<number>>;
  handleRowSelectionModelChange?: Function;
  disableRowSelectionOnClick?: boolean;
  csvFilename: string;
  columnVisibilityModel?: any;
};

type DefaultExportPropsType = {
  totalRows: number;
  loading: boolean;
  columns: GridColDef<any>[];
  rows: readonly any[];
  setFilterData: any;
  filterData: any;
  exportCSVUrl?: string;
  csvHeaders?: any;
  defaultCSVExport: true;
  currentPage: number;
  pageLimit: number;
  setCurrentPage: any;
  setPageLimit: any;
  handleRowSelectionModelChange?: any;
  disableRowSelectionOnClick?: boolean;
  csvFilename: string;
  columnVisibilityModel?: any;
};

export default function CustomDataGridPro(
  props: DefaultExportPropsType | ManualExportPropsType
) {
  let {
    rows,
    loading,
    columns,
    totalRows,
    setFilterData,
    filterData,
    csvHeaders,
    setPageLimit,
    setCurrentPage,
    pageLimit,
    currentPage,
    exportCSVUrl,
    handleRowSelectionModelChange,
    disableRowSelectionOnClick = true,
    defaultCSVExport,
    csvFilename,
    columnVisibilityModel = {},
  } = props;
  function handlePageChange(newPage: any) {
    let pageNumber = newPage.page + 1;
    let pageLimit = newPage.pageSize;

    setFilterData((preState: any) => {
      return { ...preState, page: pageNumber, size: pageLimit };
    });
    setCurrentPage(pageNumber);
    setPageLimit(pageLimit);
  }

  function handleFilterChange(filterModel: GridFilterModel) {
    let filtersObj: MultiFiltersType = {
      ...filterData,
      page: 1,
      size: pageLimit,
    };
    let searchText: string | undefined =
      filterModel?.quickFilterValues?.join(" ");
    if (filterModel?.items?.length && searchText) {
      filterModel?.items?.forEach((filterItem) => {
        let nameAgeMapping = new Map<string, string | undefined>();
        nameAgeMapping.set("col", filterItem?.field);
        nameAgeMapping.set("opt", filterItem?.operator);
        nameAgeMapping.set("value", filterItem?.value);

        if (nameAgeMapping.get("value") && nameAgeMapping.get("opt")) {
          let filter: filterColumnType = { col: "", opt: "", value: "" };
          for (let [key, value] of nameAgeMapping) {
            filter = {
              ...filter,
              [key]: value,
            };
          }
          filtersObj?.filters?.push(filter);
          filtersObj = {
            ...filtersObj,
            q: searchText,
            logicalOperator: filterModel?.logicOperator,
          };
        }
      });
    } else if (searchText) {
      filtersObj = {
        ...filtersObj,
        q: searchText,
        page: 1,
      };
    } else if (filterModel?.items?.length) {
      //inititalizing filters array in filterObj object
      filtersObj.filters = [];

      filterModel?.items?.forEach((filterItem) => {
        let nameAgeMapping = new Map<string, string | undefined>();
        nameAgeMapping.set("col", filterItem?.field);
        nameAgeMapping.set("opt", filterItem?.operator);
        nameAgeMapping.set("value", filterItem?.value);

        if (nameAgeMapping.get("value") && nameAgeMapping.get("opt")) {
          let filter: filterColumnType = { col: "", opt: "", value: "" };
          for (let [key, value] of nameAgeMapping) {
            filter = {
              ...filter,
              [key]: value,
            };
          }
          filtersObj?.filters?.push(filter);
          filtersObj = {
            ...filtersObj,
            logicalOperator: filterModel?.logicOperator,
          };
        }
      });
    } else {
      filtersObj = {
        ...filtersObj,
        page: 1,
      };
    }
    setCurrentPage(1);
    setFilterData(filtersObj);
  }

  // for csvlink
  const [csvData, setCsvData] = React.useState([]);
  const [csvLoading, setCsvLoading] = React.useState<boolean>(false);

  useEffect(() => {
    if (csvData.length) {
      csvDownloadBtn.current.link.click();
    }
  }, [csvData]);

  const compressData = () => {
    setCsvLoading(true);
    axiosInstance
      .get(exportCSVUrl ?? "", { params: { ...filterData } })
      .then((res) => {
        setCsvData(res.data);
        setCsvLoading(false);
      })
      .catch((err) => {
        setCsvLoading(false);
      });
  };

  const csvDownloadBtn = React.useRef<any>();

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
        <Grid
          item
          xs={6}
          sx={{
            textAlign: "left",
            display: "flex",
            gap: 2,
          }}
        >
          <GridToolbarFilterButton className="toolbar-tool" />

          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          {defaultCSVExport && <GridToolbarExport />}
          {!defaultCSVExport && (
            <>
              <Box
                display="none"
                ref={csvDownloadBtn}
                component={CSVLink}
                className="text_decoration_none"
                data={csvData}
                headers={csvHeaders}
                filename={csvFilename}
              ></Box>
              <Button
                startIcon={<SaveAltOutlinedIcon />}
                size="small"
                onClick={compressData}
                disabled={csvLoading ? true : false}
              >
                <Hidden smDown>
                  {csvLoading ? "Loading csv..." : "Export"}
                </Hidden>
              </Button>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography component="div" sx={{ textAlign: "right", m: 1 }}>
            <Box>
              <GridToolbarQuickFilter
                // autoFocus
                disabled={loading}
                sx={{
                  "& input::placeholder": {},
                  "& .MuiInputBase-root": {
                    backgroundColor: "transparent",
                    borderRadius: "5px",
                    border: "1px solid",
                    borderColor: "text.secondary",
                    padding: "5px",
                  },
                }}
              />
            </Box>
          </Typography>
        </Grid>
      </GridToolbarContainer>
    );
  }

  return (
    <>
      <DataGridPro
        pagination
        rows={rows}
        loading={loading}
        // autoHeight={true}
        columns={columns}
        checkboxSelection
        filterMode="server"
        rowCount={totalRows}
        sx={{ padding: "1rem", maxHeight: "80vh" }}
        paginationMode="server"
        getRowId={(rows) => rows.id}
        pageSizeOptions={[10, 50, 100, 500]}
        onFilterModelChange={handleFilterChange}
        onPaginationModelChange={handlePageChange}
        slots={{
          toolbar: CustomToolbar,
          loadingOverlay: LinearProgress,
        }}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 1200 },
          },
        }}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        initialState={{
          columns: {
            // columnVisibilityModel: columnVisibilityModel,
            columnVisibilityModel: columnVisibilityModel,
            // Hide columns status and traderName, the other columns will remain visible
          },
          pagination: {
            paginationModel: {
              pageSize: pageLimit,
              page: currentPage ? currentPage - 1 : currentPage,
            },
          },
        }}
      />
    </>
  );
}
